import { FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAccess } from '../../hooks/useAccess';
import { useVendor } from '../../hooks/useVendor';
import { IVendor, IVendorRequest } from '../../model/IVendor';
import { msg } from '../../msg';
import { IZodErrorsState } from '../../utils/checkFormStateByZod';
import Button from '../form/Button';
import FormBlock from '../form/FormBlock';
import Input, { TInputEvent } from '../form/Input';

const VendorForm: FC<{ vendor?: IVendor }> = ({ vendor }) => {
  const { addVendor, editVendor } = useVendor();
  const navigate = useNavigate();
  const isEdit = !!vendor;

  // -- Check permissions
  const { getSettingAccess } = useAccess();
  useEffect(() => {
    const allowed = isEdit ? getSettingAccess('edit-vendor') : getSettingAccess('add-vendor');
    !allowed && navigate('/vendors');
  }, []);

  // -- Init form state
  const initState: IVendorRequest = {
    name: vendor?.name || '',
    agentName: vendor?.agentName || '',
    agentPhone: vendor?.agentPhone || '',
    agentEmail: vendor?.agentEmail || '',
    externalKey: vendor?.externalKey || '',
    qboReturnsAccount: vendor?.qboReturnsAccount || '',
  };

  // -- States
  const [vendorState, setFormState] = useState<IVendorRequest>(initState);
  const [vendorErrState, setErrState] = useState<Partial<IZodErrorsState<IVendorRequest>>>();
  const [isPending, setIsPending] = useState<boolean>(() => false);

  const handle = (key: Partial<keyof typeof initState>) => (e: TInputEvent) =>
    setFormState((prevState) => ({
      ...prevState,
      [key]: e?.target?.value || '',
    }));

  const handleSubmit = async () => {
    setIsPending(() => true);
    const errs = isEdit
      ? await editVendor(vendor.id, vendorState)
      : await addVendor(vendorState);

    if (errs) {
      setErrState(() => errs);
      setIsPending(() => false);
      return;
    }
    toast.success(isEdit ? msg('vendor-edited') : msg('vendor-create'));
    return navigate('/vendors');
  };

  return (
    <div className="w-full">
      <FormBlock
        title="Vendor info"
        subTitle="Main Vendor Info"
        className=""
      >
        <Input
          label="Name (required)"
          name="name"
          placeholder='Vendor name'
          value={vendorState.name}
          handle={handle('name')}
          className='col-span-12'
          error={vendorErrState?.name}
          disabled={isPending}
        />
        <Input
          label="Agent Name"
          name="agentName"
          placeholder='Agent name'
          value={vendorState.agentName}
          handle={handle('agentName')}
          className='col-span-6'
          error={vendorErrState?.agentName}
          disabled={isPending}
        />
        <Input
          label="Agent Phone"
          name="agentPhone"
          placeholder='Agent phone'
          value={vendorState.agentPhone}
          handle={handle('agentPhone')}
          className='col-span-6'
          error={vendorErrState?.agentPhone}
          disabled={isPending}
        />
        <Input
          label="Orders Notification Email"
          name="agentEmail"
          placeholder='Orders notification email'
          value={vendorState.agentEmail}
          handle={handle('agentEmail')}
          className='col-span-6'
          error={vendorErrState?.agentEmail}
          disabled={isPending}
        />
        <Input
          label="External Key"
          name="externalKey"
          placeholder='External key'
          value={vendorState.externalKey}
          handle={handle('externalKey')}
          className='col-span-6'
          error={vendorErrState?.externalKey}
          disabled={isPending}
        />
        <Input
          label="QBO Returns Account"
          name="qboReturnsAccount"
          placeholder='QBO returns account'
          value={vendorState.qboReturnsAccount}
          handle={handle('qboReturnsAccount')}
          className='col-span-6'
          error={vendorErrState?.qboReturnsAccount}
          disabled={isPending}
        />
      </FormBlock>

      <div className="flex justify-end gap-4 mt-6 col-span-12 p-4 ">
        <Button
          onClick={() => navigate('/vendors')}
          variant="outline"
          isNotFullWidth
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          onClick={() => handleSubmit()}
          isNotFullWidth
          disabled={isPending}
        >
          {isEdit ? 'Save Vendor' : 'Add Vendor'}
        </Button>
      </div>
    </div>
  );
}

export default VendorForm;
